import { MessageBox, MessageBoxButtons, ServiceList } from '@bimser/common';
import {
	BCAvatar,
	BCButtonOverflow,
	BCDropdownWrapper,
	BCModal,
	BCNotification,
	BCScrollbars,
	BCTreeview,
	IButtonOverflowItem,
	IMenuItemClickEventArgs,
	IMenuProps,
	ITreeviewItem,
	findMessage
} from '@bimser/components';
import { downloadFile } from '@bimser/core';
import IconApprove from '@bimser/icons/16/approve';
import IconDownload from '@bimser/icons/16/download';
import IconInfo from '@bimser/icons/16/info';
import IconMore from '@bimser/icons/16/more2';
import IconSignature from '@bimser/icons/16/signature';
import IconToasterSevereWarning from '@bimser/icons/16/toaster-severe-warning';
import IconInfoBig from '@bimser/icons/64/info';
import IconSevereWarning from '@bimser/icons/64/severe-warning';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { handleServiceError } from 'modules/dm/sagas/helper';
import * as React from 'react';
import Style from '../assets/style.scss';
import { ISignatureModalProps } from '../entities';
import { ISignature } from '../entities/ISignature';
dayjs.extend(localizedFormat);

const emptyImg = require('../assets/img/signature-empty.svg');
const overflowButtonProps = { type: 'text' as const };
const toolboxDropdownProps = { placement: 'bottomLeft' as const };

const SignatureModal = (props: ISignatureModalProps) => {
	const signDocuSign = React.useRef<boolean>(props.signDocuSign ?? true);
	const signMobileSign = React.useRef<boolean>(props.signMobileSign ?? true);

	const onToolbarItemClick = (key: string) => {
		///docusign uluslarası olduğu için, mobil imzada da sadece telefon numarası ve şifre olduğu için TC kontrolü yapmadık
		if (!props.identifierNumber && key == 'digitalSign') {
			BCNotification.warning({ message: findMessage.get('103614') });
			return false;
		}

		props.onSignDocument({
			modalId: props.modalId,
			secretKey: props.secretKey,
			versionSecretKey: props.versionSecretKey,
			fileName: props.fileName,
			mimeType: props.mimeType,
			signMethod: key as any,
			signType: props.signType,
			parameters: props.parameters
		});
	};

	const getToolbarItems = (): IButtonOverflowItem[] =>
		[
			{
				key: 'digitalSign',
				buttonProps: {
					children: findMessage.get('101187'),
					onClick: () => onToolbarItemClick('digitalSign'),
					disabled: !props.permissions.digitalSignatureEnabled,
					icon: <IconSignature />,
					type: 'text'
				}
			},
			{
				...(signDocuSign.current
					? {
							key: 'docuSign',
							buttonProps: {
								children: findMessage.get('102168'),
								onClick: () => onToolbarItemClick('docuSign'),
								disabled:
									props.signType == 'flow' ||
									!props.permissions.docuSignEnabled ||
									!props.fileName.endsWith('pdf'), // TODO MimeType gelince mimeType üzerinden kontrol edilecek.
								icon: <IconSignature />,
								type: 'text'
							}
					  }
					: null)
			},
			{
				...(signMobileSign.current
					? {
							key: 'mobileSign',
							buttonProps: {
								children: findMessage.get('102137'),
								onClick: () => onToolbarItemClick('mobileSign'),
								disabled: !props.permissions.mobileSignatureEnabled,
								icon: <IconSignature />,
								type: 'text'
							}
					  }
					: null)
			}
		] as IButtonOverflowItem[];

	const onClickDownloadCertificate = (key: string, dataRef: ISignature) => {
		if (key == 'download') {
			const data = atob(dataRef.certificate?.data);
			downloadFile(
				data,
				dataRef.signOwner + '_certificate.crt',
				'application/x-x509-ca-cert'
			);
		} else if (key == 'downloadAsCMS') {
			downloadAsCMS(dataRef.signOwner);
		}
	};

	const downloadAsCMS = async (signOwner: string) => {
		const result = await ServiceList.dm.Signature.DownloadSignature.callPromise(
			{ secretKey: props.secretKey, versionSecretKey: props.versionSecretKey },
			undefined,
			undefined,
			true
		);

		if (result.data?.data) {
			if (result.data.isSigned) {
				const data = atob(result.data.data);
				downloadFile(data, signOwner + '_certificate.cms', 'application/octet-stream');
			} else {
				const { versionSecretKey, secretKey, fileName } = props;

				const response =
					await ServiceList.dm.DocumentManagement.Objects.GetDownloadUrl.handleError(
						handleServiceError
					).callPromise({
						versionSecretKey,
						secretKey,
						fileName
					});

				if (response) {
					const { downloadUrl } = response;
					const link = document.createElement('a');
					link.target = '_blank';
					link.href =
						(window as any).serviceConfig.services.dm.baseURL + '/' + downloadUrl;
					link.setAttribute('download', fileName);
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					BCNotification.warning({
						message: findMessage.get('101178'),
						autoClose: 2000
					});
				}
			}
		}
	};

	const renderPopoverContentForItem = (dataRef: ISignature) => (
		<div className={Style.SignatureValidationContainer}>
			<div className={Style.validationIcon}>
				<IconInfoBig />
			</div>
			<div className={Style.validationData}>
				<span className={Style.validationDescription}>{findMessage.get('102177')}</span>
				<div className={Style.SignatureValidationContent}>
					<BCScrollbars>
						<div className={Style.SignatureDetailsContent}>
							<span>{findMessage.get('102178') + dataRef.signerLocation}</span>
							<span>{findMessage.get('102179') + dataRef.identifierNo}</span>
							<span>{findMessage.get('102180') + dataRef.signType}</span>
							<span>{findMessage.get('102181') + dataRef.digestAlgorithm}</span>
							<span>{findMessage.get('102182') + dataRef.signatureAlgorithm}</span>
							<span>{findMessage.get('102183') + dataRef.signOwnerDescription}</span>
							<span>
								{findMessage.get('102184') +
									(dataRef.timeStamp ? dataRef.timeStamp.owner : '')}
							</span>
							<span>
								{findMessage.get('102185') +
									(dataRef.timeStamp
										? dayjs(dataRef.timeStamp.date).format('L LTS')
										: '')}
							</span>
							<span>
								{findMessage.get('102186') +
									(dataRef.timeStamp ? dataRef.timeStamp.digestAlgorithm : '')}
							</span>
							<span>{findMessage.get('102187') + dataRef.moneyLimit}</span>
							<span>{findMessage.get('102188') + dataRef.policyOID}</span>
							<span>{findMessage.get('102189') + dataRef.policyAddress}</span>
						</div>
					</BCScrollbars>
				</div>
			</div>
		</div>
	);

	const renderTreeItem = (item: ITreeviewItem<ISignature>) => {
		const signatureDetailItems: IMenuProps = {
			items: [
				{
					key: 'details',
					text: findMessage.get('102175'),
					value: 'details',
					icon: IconInfo.info
				},
				{
					key: 'download',
					text: findMessage.get('102176'),
					value: 'download',
					icon: IconDownload.info
				},
				{
					key: 'downloadAsCMS',
					text: `${findMessage.get('102176')} (cms)`,
					value: 'downloadAsCMS',
					icon: IconDownload.info
				}
			],
			onClick: (args: IMenuItemClickEventArgs) => {
				if (args.data.key == 'details') {
					MessageBox.show({
						title: findMessage.get('102177'),
						content: renderPopoverContentForItem(item.dataRef),
						width: '600px',
						buttons: MessageBoxButtons.OK
					});
				} else {
					onClickDownloadCertificate(args.data.key, item.dataRef);
				}
			}
		};

		return (
			<div
				className={Style.signatureCard}
				key={item.key}
			>
				<div className={Style.signatureInfo}>
					<span className={Style.infoColumnName}>
						<BCAvatar
							names={item.dataRef.signOwner.split(' ')}
							alt={item.dataRef.signOwner}
							size="small"
						/>{' '}
						{item.dataRef.signOwner}
					</span>
					<div className={Style.infoColumnDate}>
						<span className={Style.infoDate}>
							{dayjs(item.dataRef.signDateOffSet).format('L LTS')}
						</span>
						<span className={Style.infoStatus}>
							{item.dataRef.isValid ? (
								<IconApprove className={Style.SignatureIcons} />
							) : (
								<IconToasterSevereWarning className={Style.SignatureIcons} />
							)}
						</span>
						<div className={Style.signatureDetails}>
							<BCDropdownWrapper
								trigger={['click']}
								placement="bottomRight"
								menuProps={signatureDetailItems}
							>
								<IconMore className={Style.detailIcon} />
							</BCDropdownWrapper>
						</div>
					</div>
				</div>
			</div>
		);
	};

	const renderValidationDetail = (validationDetail: string) => {
		const rows = validationDetail.split('\n');
		return (
			<div className={Style.SignatureValidationContainer}>
				<div className={Style.validationIcon}>
					<IconSevereWarning />
				</div>
				<div className={Style.validationData}>
					<span className={Style.validationDescription}>
						{props.documentSignature.validationDescription}
					</span>
					<div className={Style.SignatureValidationContent}>
						<BCScrollbars>
							<div className={Style.validationContent}>
								{rows.map((r, index) => {
									if (r.length == 0) {
										return;
									}
									if (r.startsWith('###INVALID###')) {
										return (
											<span
												key={index.toString()}
												className={Style.invalidItem}
											>
												{r.replace('###INVALID###', '').replace(':', ':\n')}
											</span>
										);
									}
									return (
										<span key={index.toString()}>{r.replace(':', ':\n')}</span>
									);
								})}
							</div>
						</BCScrollbars>
					</div>
				</div>
			</div>
		);
	};

	const onClickValidationDetail = () => {
		MessageBox.show({
			title: findMessage.get('100652'),
			content: renderValidationDetail(props.documentSignature.validationDetail),
			width: '600px',
			buttons: MessageBoxButtons.OK
		});
	};

	const renderEmpty = (
		<div className={Style.customEmpty}>
			<img
				src={emptyImg.default}
				className={Style.emptyImg}
			/>
			<div className={Style.emptyText}>
				<span className={Style.emptyTitle}>{findMessage.get('102396')}</span>
				<span className={Style.emptyDesc}>{findMessage.get('102397')}</span>
			</div>
		</div>
	);

	const renderModalContent = () => (
		<div className={Style.signatureContainer}>
			<div className={Style.signatureHeader}>
				<BCButtonOverflow
					items={getToolbarItems()}
					overflowButtonProps={overflowButtonProps}
					dropdownProps={toolboxDropdownProps}
				/>
			</div>
			{props.documentSignature?.validationDescription && (
				<div className={Style.signatureDescription}>
					<div className={Style.signatureDescriptionText}>
						{props.documentSignature.validationDescription}
					</div>
					<div className={Style.signatureDescriptionMore}>
						{props.documentSignature?.validationDetail && (
							<IconInfo
								className={Style.SignatureIcons}
								onClick={onClickValidationDetail}
							/>
						)}
					</div>
				</div>
			)}
			<div className={Style.signatureBody}>
				<BCScrollbars>
					<div className={Style.treeHeader}>
						<span className={Style.headerSigner}>{findMessage.get('102173')}</span>
						<span className={Style.headerDate}>{findMessage.get('102174')}</span>
						<span className={Style.headerStatus}>{findMessage.get('100199')}</span>
					</div>
					<div className={Style.treeBody}>
						<BCTreeview
							disableToolbar
							items={props.documentSignature?.treeData || []}
							className={Style.signatureTreeContainer}
							customItemComponent={renderTreeItem}
							customEmptyComponent={renderEmpty}
							expandedKeys={props.documentSignature?.treeKeys || []}
						/>
					</div>
				</BCScrollbars>
			</div>
		</div>
	);

	const renderModalTitle = () => (
		<div>
			<span>{findMessage.get('102172')}</span>
		</div>
	);

	const handleOk = () => {
		props.onCloseModal(props.signStatus);
	};

	return (
		<BCModal
			id={props.modalId}
			visible
			loading={props.isLoading}
			renderHeader={renderModalTitle}
			width="850px"
			handleCancel={handleOk}
			okText={findMessage.get('100824')}
			wrapClassName={Style.SignatureModalContent}
			cancelButtonProps={{ style: { display: 'none' } }}
			modalContentWrapperCssClass={Style.smModalContent}
			handleOk={handleOk}
		>
			{renderModalContent()}
		</BCModal>
	);
};
export default SignatureModal;
